import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import QueryError from '../components/QueryError';
import M3Card from '../components/M3Card';
import M3Chip from '../components/M3Chip';
import M3LinearProgress from '../components/M3Progress';
import { UPDATE_GRAVE, DELETE_GRAVE } from '../graphql/mutations';
import { GRAVES_QUERY } from '../graphql/queries';

const GRAVE_QUERY = gql`
  query Grave($id: ID!) {
    grave(id: $id) {
      id
      identifier
      x1
      y1
      x2
      y2
      area
      perimeter
      angle
      verified
      disturbed
      depositionType
      siteId
      publicationId
      publication {
        id
        title
        author
        year
      }
      site {
        id
        name
      }
      tags {
        id
        name
      }
      goods {
        id
        type
      }
      createdAt
    }
  }
`;

export default function GraveShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GRAVE_QUERY, { variables: { id } });

  const [deleteGrave] = useMutation(DELETE_GRAVE, {
    refetchQueries: [{ query: GRAVES_QUERY }],
    async onCompleted() {
      navigate('/graves');
    }
  });

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const grave = data?.grave;
  if (!grave) return <div className="page-content">Grave not found.</div>;

  return (
    <div className="page-content detail-page">
      <Breadcrumb
        items={[
          { label: 'Graves', link: '/graves' },
          { label: grave.identifier || `Grave #${grave.id}` }
        ]}
      />

      <div className="detail-header">
        <h1>{grave.identifier || `Grave #${grave.id}`}</h1>
        <div className="detail-actions">
          <Link to={`/graves/${id}/related`} className="btn btn-secondary">
            <span className="material-symbols-rounded md-18">account_tree</span>
            Related Artefacts
          </Link>
          <Link to={`/graves/${id}/edit`} className="btn btn-secondary">
            <span className="material-symbols-rounded md-18">edit</span>
            Edit
          </Link>
          <button
            className="btn btn-text text-error"
            onClick={() => {
              if (confirm('Delete this grave?')) deleteGrave({ variables: { input: { id } } });
            }}
          >
            <span className="material-symbols-rounded md-18">delete</span>
            Delete
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Measurements" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Box</span>
                <span className="detail-value">
                  [{grave.x1}, {grave.y1}, {grave.x2}, {grave.y2}]
                </span>
              </div>
              {grave.area && (
                <div className="detail-row">
                  <span className="detail-label">Area</span>
                  <span className="detail-value">{grave.area.toFixed(2)}</span>
                </div>
              )}
              {grave.perimeter && (
                <div className="detail-row">
                  <span className="detail-label">Perimeter</span>
                  <span className="detail-value">{grave.perimeter.toFixed(2)}</span>
                </div>
              )}
              {grave.angle != null && (
                <div className="detail-row">
                  <span className="detail-label">Angle</span>
                  <span className="detail-value">{grave.angle.toFixed(1)}°</span>
                </div>
              )}
            </M3Card.Body>
          </M3Card>
        </div>

        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Status" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Verified</span>
                <span className="detail-value">{grave.verified ? '✓' : '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Disturbed</span>
                <span className="detail-value">{grave.disturbed ? 'Yes' : 'No'}</span>
              </div>
              {grave.depositionType != null && (
                <div className="detail-row">
                  <span className="detail-label">Deposition Type</span>
                  <span className="detail-value">{grave.depositionType}</span>
                </div>
              )}
            </M3Card.Body>
          </M3Card>
        </div>

        {grave.publication && (
          <div className="col-md-6">
            <M3Card variant="outlined">
              <M3Card.Header title="Publication" />
              <M3Card.Body>
                <div className="detail-row">
                  <span className="detail-label">Title</span>
                  <span className="detail-value">
                    <Link to={`/publications/${grave.publication.id}`}>
                      {grave.publication.title}
                    </Link>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Author</span>
                  <span className="detail-value">{grave.publication.author}</span>
                </div>
              </M3Card.Body>
            </M3Card>
          </div>
        )}

        {grave.site && (
          <div className="col-md-6">
            <M3Card variant="outlined">
              <M3Card.Header title="Site" />
              <M3Card.Body>
                <div className="detail-row">
                  <span className="detail-label">Name</span>
                  <span className="detail-value">
                    <Link to={`/sites/${grave.site.id}`}>{grave.site.name}</Link>
                  </span>
                </div>
              </M3Card.Body>
            </M3Card>
          </div>
        )}

        {grave.tags?.length > 0 && (
          <div className="col-md-12">
            <M3Card variant="outlined">
              <M3Card.Header title="Tags" />
              <M3Card.Body>
                <div className="d-flex gap-2 flex-wrap">
                  {grave.tags.map((tag) => (
                    <M3Chip key={tag.id} variant="input">
                      {tag.name}
                    </M3Chip>
                  ))}
                </div>
              </M3Card.Body>
            </M3Card>
          </div>
        )}

        {grave.goods?.length > 0 && (
          <div className="col-md-12">
            <M3Card variant="outlined">
              <M3Card.Header title={`Goods (${grave.goods.length})`} />
              <M3Card.Body>
                <table className="viz-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grave.goods.map((good) => (
                      <tr key={good.id}>
                        <td>{good.id}</td>
                        <td>{good.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </M3Card.Body>
            </M3Card>
          </div>
        )}
      </div>
    </div>
  );
}
